import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../csr-web-api.service';
import { ChallengeWalkthroughMeasurementReport } from '../../../models';

@Component({
  selector: 'app-challenge-walkthrough-measurements',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="measurement-panel" aria-label="Challenge walkthrough measurements">
      <h2>Challenge walkthrough measurements</h2>
      <p>Events received in the selected date range. Sessions crossing its boundaries may be partial. Data is retained for 90 days.</p>
      <div class="actions">
        <label>Challenge ID (optional) <input #challenge type="text" [value]="challengeId" maxlength="100" /></label>
        <button type="button" (click)="challengeId = challenge.value.trim(); load()" [disabled]="loading">{{ loading ? 'Loading...' : 'Refresh measurements' }}</button>
        <button type="button" (click)="download()" [disabled]="!report || loading">Export JSON</button>
      </div>
      @if (error) { <p role="alert">{{ error }}</p> }
      @if (report; as data) {
        @if (data.truncated) { <p role="alert">The event limit was reached. Narrow the date range or filter by challenge; these measurements are incomplete.</p> }
        <dl class="stats">
          <div><dt>Observed sessions</dt><dd>{{ data.openedSessions }}</dd></div>
          <div><dt>Completed observed sessions</dt><dd>{{ data.completedSessions }}</dd></div>
          <div><dt>Explicitly paused</dt><dd>{{ data.pausedSessions }}</dd></div>
          <div><dt>Inactive for 24h</dt><dd>{{ data.inactive24HourSessions }}</dd></div>
          <div><dt>Return visits, including reloads</dt><dd>{{ data.returnVisits }}</dd></div>
          <div><dt>Median active minutes</dt><dd>{{ minutes(data.medianActiveSeconds) }}</dd></div>
          <div><dt>Average active minutes</dt><dd>{{ minutes(data.averageActiveSeconds) }}</dd></div>
          <div><dt>95th percentile question AI seconds</dt><dd>{{ data.p95AiSeconds ?? 'Not measured' }}</dd></div>
          <div><dt>Question AI attempts / failures</dt><dd>{{ data.aiSamples }} / {{ data.aiFailures }}</dd></div>
          <div><dt>95th percentile final analysis seconds</dt><dd>{{ data.p95AnalysisSeconds ?? 'Not measured' }}</dd></div>
          <div><dt>Analysis attempts / failures</dt><dd>{{ data.analysisSamples }} / {{ data.analysisFailures }}</dd></div>
          <div><dt>Exact normalized question repeats</dt><dd>{{ data.repeatedQuestions }}</dd></div>
          <div><dt>Repeat topic probes</dt><dd>{{ data.repeatedTopicProbes }}</dd></div>
        </dl>
        <p>Active time is browser-reported foreground answering time, excluding breaks, request waits and idle time beyond two minutes. Wait time measures foreground turn requests; question AI timings come from the server. Final analysis includes topic batches and reuse of evidence. Results wait includes queueing, analysis and finalization. Inactivity is an abandonment signal, not a confirmed drop-out. Repeat topic probes may be intentional clarifications.</p>
        <p>{{ data.eventCount }} events; showing {{ data.sessions.length }} of {{ data.sessionCount }} sessions. Missing measurements are not zero. Completion includes early finishes. The export includes completion reasons, task mix, fallback counts and coverage snapshots. Cumulative visit snapshots can include activity before the date range.</p>
        <div class="table-wrap" tabindex="0" aria-label="Walkthrough session measurements">
          <table>
            <thead><tr><th>Session / challenge</th><th>Status</th><th>Active min</th><th>Wait sec</th><th>Results wait sec</th><th>Visits / pauses</th><th>Turns</th><th>Selected / verified / unresolved topics</th><th>Question repeats / fallbacks</th></tr></thead>
            <tbody>
              @for (session of data.sessions; track session.sessionId) {
                <tr><td><code>{{ session.sessionId }}</code><br /><small>{{ session.challengeId }}</small></td>
                  <td>{{ session.status }}</td><td>{{ minutes(session.activeSeconds) }}</td><td>{{ session.waitSeconds ?? 'Not measured' }}</td>
                  <td>{{ session.resultsWaitSeconds ?? 'Not measured' }}</td>
                  <td>{{ session.visits }} / {{ session.pauses }}</td><td>{{ session.turnCount }}</td>
                  <td>{{ session.selectedTopics }} / {{ session.verifiedTopics }} / {{ session.unresolvedTopics }}</td>
                  <td>{{ session.repeatedQuestions }} / {{ session.fallbackQuestions }}</td></tr>
              } @empty { <tr><td colspan="9">No walkthrough measurements in this date range.</td></tr> }
            </tbody>
          </table>
        </div>
      }
    </section>
  `,
  styles: [`
    .measurement-panel { margin: 1.5rem 0; padding: 1.25rem; background: white; border: 1px solid #cbd5e1; border-radius: 1rem; }
    h2 { font-size: 1.15rem; font-weight: 700; } p { font-size: .8rem; margin: .75rem 0; color: #475569; }
    .actions { display: flex; gap: .75rem; align-items: end; flex-wrap: wrap; margin: 1rem 0; }
    label { display: grid; font-size: .8rem; gap: .25rem; } input, button { padding: .5rem; border: 1px solid #94a3b8; border-radius: .5rem; }
    button { cursor: pointer; font-size: .8rem; } button:disabled { opacity: .5; cursor: default; }
    .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 1rem; margin: 1rem 0; }
    dt { font-size: .75rem; color: #475569; } dd { font-size: 1.2rem; font-weight: 700; }
    .table-wrap { overflow: auto; } table { width: 100%; font-size: .75rem; border-collapse: collapse; }
    td, th { padding: .6rem; text-align: left; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  `]
})
export class ChallengeWalkthroughMeasurementsComponent implements OnChanges {
  private readonly api = inject(CsrWebApiService);
  @Input() fromDate?: string | null;
  @Input() toDate?: string | null;
  challengeId = '';
  loading = false;
  error = '';
  report: ChallengeWalkthroughMeasurementReport | null = null;
  private revision = 0;

  ngOnChanges(): void { void this.load(); }
  minutes(seconds: number | null): string { return seconds === null ? 'Not measured' : (seconds / 60).toFixed(1); }

  async load(): Promise<void> {
    if (!this.fromDate || !this.toDate) return;
    const revision = ++this.revision;
    this.loading = true; this.error = ''; this.report = null;
    try {
      const report = await firstValueFrom(this.api.getChallengeWalkthroughMeasurements(this.fromDate, this.toDate, this.challengeId || undefined));
      if (revision === this.revision) this.report = report;
    } catch { if (revision === this.revision) this.error = 'Could not load measurements. Choose a range of up to 90 days and retry.'; }
    finally { if (revision === this.revision) this.loading = false; }
  }

  download(): void {
    if (!this.report) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(this.report, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a'); link.href = url; link.download = 'challenge-walkthrough-measurements.json';
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
